import React, { useState, useEffect, useRef } from 'react';
import { X, Scissors, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';
import { gallery } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

export const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [visibleImages, setVisibleImages] = useState([]);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gallery.forEach((image, index) => {
              setTimeout(() => {
                setVisibleImages((prev) => [...prev, image.id]);
              }, index * 150);
            });
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handlePrevImage = (e) => {
    e.stopPropagation();
    const currentIndex = gallery.findIndex(img => img.id === selectedImage.id);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : gallery.length - 1;
    setSelectedImage(gallery[prevIndex]);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    const currentIndex = gallery.findIndex(img => img.id === selectedImage.id);
    const nextIndex = currentIndex < gallery.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(gallery[nextIndex]);
  };

  return (
    <section id="galerie" ref={sectionRef} className="py-24 bg-[#050505] relative overflow-hidden">
      {/* Animated background effects */}
      <div className="absolute top-1/4 left-0 w-1/2 h-1/2 bg-gradient-to-r from-[#00D9FF]/10 to-transparent blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
      <div className="absolute top-1/2 right-0 w-1/2 h-1/2 bg-gradient-to-l from-[#FF1493]/10 to-transparent blur-3xl animate-pulse" style={{ animationDuration: '5s', animationDelay: '1s' }} />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20 animate-fade-in">
          <div className="inline-block mb-6">
            <div className="flex items-center gap-3 px-6 py-3 rounded-full glass-effect border border-white/20 backdrop-blur-xl">
              <Scissors className="w-5 h-5 text-[#00D9FF] animate-pulse" />
              <span className="text-sm font-bold gradient-text tracking-widest">{t.gallery.badge}</span>
            </div>
          </div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 animate-slide-in-left" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {t.gallery.title} <span className="gradient-text">{t.gallery.titleAccent}</span>
          </h2>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto leading-relaxed animate-slide-in-right" style={{ animationDelay: '0.2s' }}>
            {t.gallery.subtitle}
          </p>
        </div>

        {/* Gallery Grid with enhanced animations */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {gallery.map((image, index) => (
            <div
              key={image.id}
              className={`group relative aspect-square overflow-hidden rounded-2xl cursor-pointer bg-[#121212] transition-all duration-700 ${
                visibleImages.includes(image.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              onClick={() => setSelectedImage(image)}
              style={{
                transitionDelay: `${index * 150}ms`
              }}
            >
              {/* Image with zoom and tilt effect */}
              <div className="w-full h-full overflow-hidden">
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-125 group-hover:rotate-3 filter group-hover:brightness-110"
                />
              </div>
              
              {/* Multi-layer gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
              
              {/* Animated gradient border on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00D9FF]/30 via-[#5B7FFF]/20 to-[#FF1493]/30" />
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00D9FF] via-[#5B7FFF] to-[#FF1493]" />
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#FF1493] via-[#5B7FFF] to-[#00D9FF]" />
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#00D9FF] to-[#FF1493]" />
                <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-[#FF1493] to-[#00D9FF]" />
              </div>
              
              {/* Floating zoom icon with pulse effect */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 transform scale-50 group-hover:scale-100">
                <div className="glass-effect p-5 rounded-full border-2 border-white/30 backdrop-blur-xl shadow-2xl">
                  <ZoomIn className="w-10 h-10 text-white animate-pulse" />
                </div>
              </div>
              
              {/* Animated text reveal */}
              <div className="absolute bottom-0 left-0 right-0 p-5 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                <div className="glass-effect p-3 rounded-lg border border-white/20 backdrop-blur-xl">
                  <p className="text-white text-sm font-semibold">{image.alt}</p>
                </div>
              </div>

              {/* Corner accent */}
              <div className="absolute top-3 right-3 w-8 h-8 border-t-2 border-r-2 border-[#00D9FF] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-3 left-3 w-8 h-8 border-b-2 border-l-2 border-[#FF1493] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
          ))}
        </div>
      </div>

      {/* Enhanced Lightbox Modal with navigation */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSelectedImage(null)}
        >
          {/* Close button with enhanced style */}
          <button
            className="absolute top-6 right-6 p-4 glass-effect rounded-full text-white hover:bg-white/20 transition-all duration-300 hover:scale-110 hover:rotate-90 z-50 border border-white/20"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-7 h-7" />
          </button>
          
          {/* Previous button */}
          <button
            className="absolute left-6 top-1/2 -translate-y-1/2 p-4 glass-effect rounded-full text-white hover:bg-white/20 transition-all duration-300 hover:scale-110 z-50 border border-white/20"
            onClick={handlePrevImage}
          >
            <ChevronLeft className="w-7 h-7" />
          </button>

          {/* Next button */}
          <button
            className="absolute right-6 top-1/2 -translate-y-1/2 p-4 glass-effect rounded-full text-white hover:bg-white/20 transition-all duration-300 hover:scale-110 z-50 border border-white/20"
            onClick={handleNextImage}
          >
            <ChevronRight className="w-7 h-7" />
          </button>
          
          {/* Image container with enhanced styling */}
          <div className="relative max-w-6xl w-full">
            <div className="relative rounded-2xl overflow-hidden border-2 border-white/20 shadow-2xl">
              <img
                src={selectedImage.url}
                alt={selectedImage.alt}
                className="max-w-full max-h-[85vh] object-contain mx-auto"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Gradient corners */}
              <div className="absolute top-0 left-0 w-20 h-20 bg-gradient-to-br from-[#00D9FF]/50 to-transparent" />
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-[#FF1493]/50 to-transparent" />
              <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-[#5B7FFF]/50 to-transparent" />
              <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-[#00D9FF]/50 to-transparent" />
            </div>
            
            {/* Image info with gradient */}
            <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black via-black/80 to-transparent">
              <div className="glass-effect p-4 rounded-xl border border-white/20 backdrop-blur-xl">
                <p className="text-white text-xl font-bold text-center gradient-text">{selectedImage.alt}</p>
              </div>
            </div>
          </div>

          {/* Image counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 glass-effect px-6 py-3 rounded-full border border-white/20 backdrop-blur-xl">
            <span className="text-white font-semibold">
              {gallery.findIndex(img => img.id === selectedImage.id) + 1} / {gallery.length}
            </span>
          </div>
        </div>
      )}
    </section>
  );
};
